import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import MoneyInput from '../../../common/money-input/money-input';
import axios from 'axios';
import React, { useContext, useState, useEffect } from 'react';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { MerchantType } from '../../../../interfaces/delivery-company/merchants';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import { InvoiceFrequencyPeriodType, InvoiceFrequencyPeriodTypeLabels } from '../../../../enums/extra-charges';
import { Money, displayMoney } from '../../../../utils/money';
import { useSnackBar } from '../../../../contexts/snackbar';

interface InvoiceFrequencyDialogProps {
  open: boolean;
  onClose: () => void;
  merchant: MerchantType;
  onUpdate?: (updatedMerchant: MerchantType) => void;
}

function InvoiceFrequencyDialog({
  open,
  onClose,
  merchant,
  onUpdate,
}: InvoiceFrequencyDialogProps) {
  const { config } = useContext(AuthContext);
  const { settings: orgSettings } = useContext(OrgSettingsContext);
  const { showMessage } = useSnackBar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine if merchant has custom settings
  const hasCustomSettings = merchant.invoice_frequency_enabled !== null && merchant.invoice_frequency_enabled !== undefined;
  
  // Form state
  const [configType, setConfigType] = useState<'default' | 'custom'>(hasCustomSettings ? 'custom' : 'default');
  const [enabled, setEnabled] = useState(merchant.invoice_frequency_enabled ?? false);
  const [periodType, setPeriodType] = useState<InvoiceFrequencyPeriodType>(
    merchant.invoice_frequency_period_type || InvoiceFrequencyPeriodType.WEEK
  );
  const [limit, setLimit] = useState<string>(merchant.invoice_frequency_limit?.toString() || '');
  const [chargeAmount, setChargeAmount] = useState<Money[]>(merchant.invoice_frequency_charge_amount ?? []);

  // Reset form when dialog opens or merchant changes
  useEffect(() => {
    if (open) {
      const hasCustom = merchant.invoice_frequency_enabled !== null && merchant.invoice_frequency_enabled !== undefined;
      setConfigType(hasCustom ? 'custom' : 'default');
      setEnabled(merchant.invoice_frequency_enabled ?? false);
      setPeriodType(merchant.invoice_frequency_period_type || InvoiceFrequencyPeriodType.WEEK);
      setLimit(merchant.invoice_frequency_limit?.toString() || '');
      setChargeAmount(merchant.invoice_frequency_charge_amount ?? []);
      setError(null);
    }
  }, [open, merchant]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    
    console.log('Form submitted, starting save process...');
    
    // Check if charge amount is provided when enabled
    if (configType === 'custom' && enabled && (!chargeAmount || chargeAmount.length === 0)) {
      setError('Charge amount is required when invoice frequency charges are enabled');
      return;
    }

    console.log('Setting loading to true...');
    setLoading(true);
    setError(null);

    try {
      const updateData = configType === 'default' 
        ? {
            invoice_frequency_enabled: null,
            invoice_frequency_period_type: null,
            invoice_frequency_limit: null,
            invoice_frequency_charge_amount: null,
          }
        : {
            invoice_frequency_enabled: enabled,
            invoice_frequency_period_type: enabled ? periodType : null,
            invoice_frequency_limit: enabled ? parseInt(limit) : null,
            invoice_frequency_charge_amount: enabled ? chargeAmount : null,
          };

      console.log('Sending update data:', updateData);
      console.log('API endpoint:', `${API_ENDPOINT}/api/v1/merchant/${merchant.id}/`);

      const response = await axios.patch(
        `${API_ENDPOINT}/api/v1/merchant/${merchant.id}/`,
        updateData,
        config
      );

      console.log('Response received:', response.data);

      // Update the merchant data first
      if (onUpdate) {
        console.log('Calling onUpdate with response data...');
        onUpdate(response.data);
      }

      // Show success message
      showMessage('Invoice frequency settings updated successfully', 'success');
      
      console.log('About to close dialog...');
      // Close dialog only after successful update
      onClose();
    } catch (err) {
      console.error('Error updating invoice frequency settings:', err);
      setError('Failed to update settings. Please try again.');
      showMessage('Failed to update settings', 'error');
    } finally {
      console.log('Setting loading to false...');
      setLoading(false);
    }
  };


  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Configure Invoice Frequency Extra Charges</DialogTitle>
        
        <DialogContent>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Configuration Type */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Configuration Type
              </Typography>
              <RadioGroup
                value={configType}
                onChange={(e) => setConfigType(e.target.value as 'default' | 'custom')}
              >
                <FormControlLabel 
                  value="default" 
                  control={<Radio />} 
                  label={`Use Organization Default (${orgSettings.invoice_frequency_enabled ? 'Enabled' : 'Disabled'})`}
                />
                <FormControlLabel 
                  value="custom" 
                  control={<Radio />} 
                  label="Custom Settings" 
                />
              </RadioGroup>
            </Box>

            {configType === 'custom' && (
              <>
                {/* Enable/Disable */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Enable Invoice Frequency Charges
                  </Typography>
                  <RadioGroup
                    value={enabled ? 'enabled' : 'disabled'}
                    onChange={(e) => setEnabled(e.target.value === 'enabled')}
                  >
                    <FormControlLabel value="enabled" control={<Radio />} label="Enabled" />
                    <FormControlLabel value="disabled" control={<Radio />} label="Disabled" />
                  </RadioGroup>
                </Box>

                {enabled && (
                  <>
                    {/* Period Type */}
                    <FormControl fullWidth required>
                      <InputLabel>Period Type</InputLabel>
                      <Select
                        value={periodType}
                        label="Period Type"
                        onChange={(e) => setPeriodType(e.target.value as InvoiceFrequencyPeriodType)}
                        required
                      >
                        {Object.entries(InvoiceFrequencyPeriodTypeLabels).map(([value, label]) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Limit */}
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Invoice Limit (per period)"
                      value={limit}
                      onChange={(e) => setLimit(e.target.value)}
                      inputProps={{ min: 0 }}
                      helperText="Maximum number of invoices allowed per period before extra charges apply"
                    />

                    {/* Charge Amount */}
                    <Box>
                      <MoneyInput
                        label="Amount to charge for each invoice exceeding the limit *"
                        money={chargeAmount}
                        setMoney={setChargeAmount}
                      />
                    </Box>
                  </>
                )}
              </>
            )}

            {configType === 'default' && orgSettings.invoice_frequency_enabled && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Organization Default Settings:</strong><br />
                  • Period: {orgSettings.invoice_frequency_period_type ? InvoiceFrequencyPeriodTypeLabels[orgSettings.invoice_frequency_period_type as keyof typeof InvoiceFrequencyPeriodTypeLabels] : 'Not set'}<br />
                  • Limit: {orgSettings.invoice_frequency_limit !== null && orgSettings.invoice_frequency_limit !== undefined ? `${orgSettings.invoice_frequency_limit} invoices` : 'Not set'}<br />
                  • Charge: {orgSettings.invoice_frequency_charge_amount && orgSettings.invoice_frequency_charge_amount.length > 0 ? displayMoney(orgSettings.invoice_frequency_charge_amount) : 'Not set'}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button 
            type="button"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogActions>
      </form>
    </CustomDialog>
  );
}

export default InvoiceFrequencyDialog;
